import { useEffect, useState } from "react";
import Icon from "@/components/feature/Icon";
import SmartImage from "@/components/feature/SmartImage";

function FloatingOrbs() {
  return (
    <>
      <div className="absolute top-[10%] right-[25%] w-64 h-64 rounded-full bg-white/[0.03] blur-3xl animate-float-1" />
      <div className="absolute bottom-[20%] left-[10%] w-72 h-72 rounded-full bg-silver-300/[0.03] blur-3xl animate-float-2" />
      <div className="absolute top-[55%] right-[5%] w-48 h-48 rounded-full bg-white/[0.02] blur-2xl animate-float-3" />
    </>
  );
}

function TechScanLine() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-[0.04]">
      <div className="absolute left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-white to-transparent animate-scan-line" />
    </div>
  );
}

function TechParticles() {
  const particles = [
    { left: "8%", top: "22%", delay: "0s", size: 2 },
    { left: "22%", top: "58%", delay: "1.3s", size: 3 },
    { left: "38%", top: "32%", delay: "2.0s", size: 2 },
    { left: "52%", top: "72%", delay: "0.7s", size: 1.5 },
    { left: "68%", top: "18%", delay: "1.6s", size: 2.5 },
    { left: "85%", top: "48%", delay: "2.4s", size: 2 },
    { left: "15%", top: "82%", delay: "3.1s", size: 1.5 },
    { left: "62%", top: "85%", delay: "1.9s", size: 2 },
    { left: "78%", top: "28%", delay: "2.7s", size: 3 },
    { left: "42%", top: "92%", delay: "0.4s", size: 1.5 },
  ];
  return (
    <div className="absolute inset-0 pointer-events-none hidden md:block">
      {particles.map((p, i) => (
        <div
          key={i}
          className="absolute rounded-full bg-white/60 animate-tech-particle"
          style={{
            left: p.left,
            top: p.top,
            width: p.size,
            height: p.size,
            animationDelay: p.delay,
            animationDuration: `${3 + (i % 3) * 1.5}s`,
          }}
        />
      ))}
    </div>
  );
}

export default function EventsHero() {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative bg-navy-950 overflow-hidden">
      <FloatingOrbs />
      <TechScanLine />
      <TechParticles />

      <div className="absolute inset-0">
        <SmartImage
          src="https://readdy.ai/api/search-image?query=Abstract%20futuristic%20technology%20background%20with%20geometric%20digital%20interface%20elements%20flowing%20data%20streams%20hexagonal%20grid%20patterns%20holographic%20light%20beams%20circuit%20board%20traces%20deep%20dark%20navy%20black%20background%20silver%20and%20white%20luminous%20accents%20high%20contrast%20minimalist%20corporate%20sci-fi%20aesthetic%20clean%20geometric%20shapes%20only%20no%20organic%20brain%20forms%20professional%20technology%20concept%20art%20cinematic%20wide%20composition%20without%20people&width=1400&height=700&seq=taiaa-events-hero-v2&orientation=landscape"
          alt="活動現場"
          className="w-full h-full object-cover opacity-40"
          hideOnError
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-950/85 via-navy-950/65 to-navy-950/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-transparent to-navy-950/40" />
      </div>

      <div className="absolute inset-0 pointer-events-none opacity-[0.08]">
        <div
          className="w-full h-full animate-grid-move"
          style={{
            backgroundImage:
              "linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)",
            backgroundSize: "100px 100px",
          }}
        />
      </div>

      <div className="relative z-10 px-6 md:px-10 lg:px-16 pt-32 pb-10 md:pt-40 md:pb-14">
        <div className="max-w-4xl">
          <p
            className={`text-sm font-medium tracking-[0.2em] text-silver-400 uppercase mb-4 transition-all duration-700 ${
              loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            Events & News
          </p>
          <h1
            className={`font-serif text-4xl md:text-5xl lg:text-6xl font-semibold text-white leading-tight mb-6 transition-all duration-700 delay-150 animate-text-glow ${
              loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            活動與最新消息
          </h1>
          <p
            className={`text-silver-300 text-base md:text-lg leading-relaxed max-w-2xl mb-8 transition-all duration-700 delay-300 ${
              loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            TAIAA 定期舉辦論壇、研討會、工作坊與公益活動，串連產業脈動。在此掌握最新協會公告、產業觀察與政策建言。
          </p>

          <div
            className={`flex flex-wrap gap-4 transition-all duration-700 delay-[450ms] ${
              loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            <a
              href="#events"
              className="inline-flex items-center gap-2 bg-white text-navy-950 px-7 py-3.5 rounded-full text-sm font-medium hover:bg-silver-100 transition-colors whitespace-nowrap"
            >
              近期活動
              <Icon className="ri-arrow-down-line" />
            </a>
            <a
              href="#news"
              className="inline-flex items-center gap-2 border border-white/30 text-white px-7 py-3.5 rounded-full text-sm font-medium hover:bg-white/10 transition-colors whitespace-nowrap"
            >
              最新消息
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}